/**
 * Privacy Requests admin queue (GDPR / CCPA).
 *
 * Route: /projects/:projectId/privacy-requests
 *
 * Lets the admin triage data-subject requests (export, delete, access,
 * rectify, opt-out). Every request is per-project — queries filter by the
 * projectId in the URL, so work on one store never touches another.
 *
 * Features:
 *   - filter by status / type / overdue
 *   - detail drawer with full status history + internal note
 *   - advance status with required adjacency-graph enforcement (server-side)
 *   - download GDPR Article 15 export as JSON
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { apiHelpers, type PrivacyRequest } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  verifying: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  rejected: 'bg-gray-100 text-gray-800',
};

const NEXT_STATUSES: Record<string, string[]> = {
  pending: ['verifying', 'in_progress', 'rejected'],
  verifying: ['in_progress', 'rejected'],
  in_progress: ['completed', 'rejected'],
  completed: [],
  rejected: [],
};

export default function PrivacyRequestsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [selected, setSelected] = useState<PrivacyRequest | null>(null);
  const [advanceTarget, setAdvanceTarget] = useState<string>('');
  const [advanceNote, setAdvanceNote] = useState('');
  const [internalNote, setInternalNote] = useState('');

  const listQuery = useQuery({
    queryKey: ['privacy-requests', projectId, statusFilter, typeFilter, overdueOnly],
    queryFn: async () => {
      const params: any = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (overdueOnly) params.overdue = 'true';
      const res = await apiHelpers.listPrivacyRequests(projectId!, params);
      return (res.data.data || []) as PrivacyRequest[];
    },
    enabled: Boolean(projectId),
  });

  const detailQuery = useQuery({
    queryKey: ['privacy-request', projectId, selected?._id],
    queryFn: async () => {
      const res = await apiHelpers.getPrivacyRequest(projectId!, selected!._id);
      const row = res.data.data as PrivacyRequest;
      setInternalNote(row.internalNote || '');
      return row;
    },
    enabled: Boolean(projectId) && Boolean(selected?._id),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['privacy-requests', projectId] });

  const advanceMutation = useMutation({
    mutationFn: () =>
      apiHelpers.advancePrivacyRequest(projectId!, selected!._id, {
        status: advanceTarget,
        note: advanceNote || undefined,
      }),
    onSuccess: () => {
      toast.success('Status advanced');
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['privacy-request', projectId, selected?._id] });
      setAdvanceTarget('');
      setAdvanceNote('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to advance status');
    },
  });

  const noteMutation = useMutation({
    mutationFn: () =>
      apiHelpers.updatePrivacyRequestNote(projectId!, selected!._id, {
        internalNote: internalNote || undefined,
      }),
    onSuccess: () => {
      toast.success('Internal note saved');
      queryClient.invalidateQueries({ queryKey: ['privacy-request', projectId, selected?._id] });
    },
    onError: () => toast.error('Failed to save note'),
  });

  const handleExport = async () => {
    if (!selected) return;
    try {
      const res = await apiHelpers.exportPrivacyRequest(projectId!, selected._id);
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `privacy-export-${selected._id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const items = listQuery.data || [];
  const overdueCount = items.filter((r) => r.overdue).length;

  const detail = detailQuery.data || selected;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Privacy Requests</h1>
        <p className="text-muted-foreground">
          GDPR / CCPA data-subject requests submitted from your storefront. Respond within 30 days.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Open</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {items.filter((r) => !['completed', 'rejected'].includes(r.status)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold">{overdueCount}</div>
              {overdueCount > 0 && <ShieldAlert className="h-5 w-5 text-red-500" />}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {items.filter((r) => r.status === 'completed').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total (visible)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{items.length}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Queue</CardTitle>
              <CardDescription>Sorted by response deadline (soonest first)</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verifying">Verifying</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="export">Export</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="access">Access</SelectItem>
                  <SelectItem value="rectify">Rectify</SelectItem>
                  <SelectItem value="opt-out">Opt-out</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={overdueOnly ? 'default' : 'outline'}
                onClick={() => setOverdueOnly(!overdueOnly)}
              >
                Overdue only
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-muted-foreground py-12 text-center">No requests match.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Respond by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow
                    key={r._id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelected(r)}
                  >
                    <TableCell className="font-medium">{r.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{r.requestType}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLOR[r.status] || ''}>{r.status}</Badge>
                      {r.overdue && (
                        <Badge variant="destructive" className="ml-2">
                          Overdue
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{format(new Date(r.createdAt), 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      {r.respondByAt ? format(new Date(r.respondByAt), 'MMM d, yyyy') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) {
            setSelected(null);
            setAdvanceTarget('');
            setAdvanceNote('');
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Privacy request</DialogTitle>
            <DialogDescription>
              {selected &&
                `${selected.requestType} • submitted ${format(
                  new Date(selected.createdAt),
                  'MMM d, yyyy h:mm a'
                )}`}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email" value={detail.email} />
                <Field label="Full name" value={detail.fullName || '-'} />
                <Field
                  label="Status"
                  value={
                    <Badge className={STATUS_COLOR[detail.status] || ''}>{detail.status}</Badge>
                  }
                />
                <Field
                  label="Respond by"
                  value={
                    detail.respondByAt ? format(new Date(detail.respondByAt), 'MMM d, yyyy') : '-'
                  }
                />
              </div>

              {detail.detail && (
                <div>
                  <Label className="text-muted-foreground text-xs">Customer note</Label>
                  <div className="bg-muted/40 mt-1 whitespace-pre-wrap rounded-md border p-3 text-sm">
                    {detail.detail}
                  </div>
                </div>
              )}

              {detail.statusHistory && detail.statusHistory.length > 0 && (
                <div>
                  <Label className="text-muted-foreground text-xs">Status history</Label>
                  <div className="mt-1 space-y-2">
                    {detail.statusHistory.map((h, i) => (
                      <div key={i} className="rounded-md border p-2 text-sm">
                        <div className="flex items-center justify-between">
                          <Badge className={STATUS_COLOR[h.status] || ''}>{h.status}</Badge>
                          <span className="text-muted-foreground text-xs">
                            {format(new Date(h.changedAt), 'MMM d, yyyy h:mm a')} · {h.changedBy}
                          </span>
                        </div>
                        {h.note && <p className="text-muted-foreground mt-1">{h.note}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="int-note">Internal note (not shown to customer)</Label>
                <Textarea
                  id="int-note"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  rows={3}
                  maxLength={5000}
                  className="mt-1"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => noteMutation.mutate()}
                  disabled={noteMutation.isPending}
                >
                  Save note
                </Button>
              </div>

              {NEXT_STATUSES[detail.status]?.length > 0 && (
                <div className="rounded-md border p-3">
                  <Label>Advance status</Label>
                  <div className="mt-2 flex gap-2">
                    <Select value={advanceTarget} onValueChange={setAdvanceTarget}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Choose next status" />
                      </SelectTrigger>
                      <SelectContent>
                        {NEXT_STATUSES[detail.status].map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Textarea
                      value={advanceNote}
                      onChange={(e) => setAdvanceNote(e.target.value)}
                      placeholder="Note (optional)"
                      rows={1}
                      maxLength={2000}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => advanceMutation.mutate()}
                      disabled={!advanceTarget || advanceMutation.isPending}
                    >
                      Advance
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Download export JSON
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Label className="text-muted-foreground text-xs">{label}</Label>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}
